import { Component, OnInit, Input, EventEmitter, Output, OnChanges, signal, computed } from '@angular/core';
import { FlowObjectDefinition, FlowObjectInstance, FlowObjectInstanceState } from '../../../models/flow-object.model';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';
import { ConfigSchema } from '../../../models/config-schema.model';
import { FlowDefinition, FlowInstance } from '../../../models/flow.model';
import { InputDataTaskOutboundApi, OutputDataTaskOutboundApi } from '../../../models/input-output-data.model';
import { AcessoCidadaoService } from '../../../services/acesso-cidadao.service';
import { AuthService } from '../../../services/auth.service';
import { EDocsService } from '../../../services/edocs.service';
import { FlowInstanceService } from '../../../services/flow-instance.service';
import { FlowObjectInstanceService } from '../../../services/flow-object-instance.service';
import { Enums } from '../../../shared/enums';
import { Utils } from '../../../shared/utils';
import { IMPORTS } from './_imports';

@Component({
    selector: 'fob-outbound-api',
    templateUrl: './fob-outbound-api.component.html',
    styleUrl: './fob-outbound-api.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class FlowObjectOutboundApiComponent implements OnInit, OnChanges {
    // #region [Type props]
    FlowObjectInstanceState = FlowObjectInstanceState;
    Utils = Utils;
    Enums = Enums;
    // #endregion

    // #region [const]
    MAX_RETRIES: number = 25 as const;
    RETRY_INTERVAL_SECONDS: number = 3 as const;
    // #endregion

    // #region [props]
    inputData: InputDataTaskOutboundApi;
    forwardingProtocol: string = Enums.Messages.DefaultEmptyValue;
    processProtocol: string = Enums.Messages.DefaultEmptyValue;
    interval: any;
    // #endregion

    // #region [signals]
    $model = signal(null as FlowObjectInstance);
    $flowInstance = signal(null as FlowInstance);
    $flowObjectDefinition = signal(null as FlowObjectDefinition);
    $message = signal('');
    $isMessageFromApi = signal(false);
    $isPollingFinished = signal(false);
    $isResolver = signal(false);
    $publicMessageHtml = signal(null as string);
    $hasEnqueuedAtFirst = signal(false);
    $pollingCount = signal(0);
    // #endregion

    // #region [computeds]
    $currentUser = computed(() => this.authService.$user());
    $isPublicMessageHtmlLoading = computed(() => Utils.isNullOrEmpty(this.$publicMessageHtml()));
    $configSchema = computed(() => JSON.parse(this.$model().flowObjectDefinition.configSchema) as ConfigSchema);
    $pollingProgress = computed(() => this.$hasEnqueuedAtFirst() == false
        ? this.$pollingCount() * (100 / this.MAX_RETRIES)
        : 100 - (this.$pollingCount() * (100 / this.MAX_RETRIES))
    );
    $isFinished = computed(() =>
        [
            FlowObjectInstanceState.Finished,
            FlowObjectInstanceState.ManuallyFinished,
            FlowObjectInstanceState.AutomaticallyCancelled,
            FlowObjectInstanceState.ManuallyCancelled
        ].includes(this.$model().stateId)
    );
    // #endregion

    // #region [Input/Output]
    @Input() _model: FlowObjectInstance;
    @Input() _flowInstance: FlowInstance;
    @Input() _flowDefinition: FlowDefinition;
    @Output() _modelChangeEvent = new EventEmitter<FlowObjectInstance>();
    // #endregion

    constructor(
        private toastr: ToastrService,
        private confirmationService: ConfirmationService,
        private cookieService: CookieService,
        private authService: AuthService,
        private acessoCidadaoService: AcessoCidadaoService,
        private flowObjectInstanceService: FlowObjectInstanceService,
        private flowInstanceService: FlowInstanceService,
        private eDocsService: EDocsService
    ) { }

    async ngOnInit() {
        Utils.setSignal(this.$flowObjectDefinition, this._flowDefinition.flowObjectDefinitions.find(x => x.id == this.$model().flowObjectDefinitionId));
        this.inputData = JSON.parse(this.$model().inputData) as InputDataTaskOutboundApi;

        // #region [protocolo de Encaminhamento]
        this.forwardingProtocol = this.$flowInstance().rootForwardingInfo;
        // #endregion

        // #region [protocolo de Processo]
        if (Utils.isNotNullOrEmpty(this.inputData.eDocsData.processId)) {
            this.processProtocol = this.$flowInstance().rootProcessInfo;
        }
        // #endregion

        // #region [Ator Resolvedor]
        if (this.$isResolver() == false) {
            if (!this.$currentUser().isPublicAgent) {
                this.$isResolver.set(false);
            } else if (this.$configSchema().taskOutboundApi.resolver.id != null) {
                const response = await this.acessoCidadaoService.getConjuntoAgentesPublicos(this.$configSchema().taskOutboundApi.resolver.id);

                if (!response.isSuccess) {
                    this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                if (response.data.some(x => x.sub.toLowerCase() == this.$currentUser().id.toLowerCase())) {
                    this.$isResolver.set(true);
                } else {
                    this.$isResolver.set(false);
                }
            }
        }
        // #endregion

        this.fillMessage();

        if (this.$isFinished()) return;

        if (this.$model().stateId == FlowObjectInstanceState.Started) {
            this.pollForUpdate();
        } else {
            this.$isPollingFinished.set(true);
        }

        // #region [mensagem pública customizada]
        if (Utils.isNotNullOrEmpty(this.$flowObjectDefinition()?.publicMessageHtml)) {
            setTimeout(() => this.$publicMessageHtml.set(Utils.getParsedMessageHtml(
                this.cookieService,
                this.$flowObjectDefinition().publicMessageHtml,
                this.forwardingProtocol,
                this.processProtocol
            )), 1000);
        }
        // #endregion
    }

    ngOnChanges() {
        this.$model.set(this._model);
        this.$flowInstance.set(this._flowInstance);
    }

    async enqueueTask() {
        this.confirmationService.confirm({
            message: Enums.Messages.ConfirmEnqueueTask,
            accept: this.processEnqueue,
            reject: () => {
                this.confirmationService.close();
            }
        });
    }

    async updateTask(stateId: number) {
        this.confirmationService.confirm({
            message: stateId == FlowObjectInstanceState.ManuallyFinished ? Enums.Messages.ConfirmFinishTask : Enums.Messages.ConfirmCancelTask,
            accept: async () => {
                let currentStateId = this.$model().stateId;
                Utils.updSignal(this.$model, x => {
                    x.stateId = stateId;
                    return x;
                });

                const response = await this.flowInstanceService.manuallyFinishOrCancel(this.$model());

                if (!response.isSuccess) {
                    Utils.updSignal(this.$model, x => {
                        x.stateId = currentStateId;
                        return x;
                    });
                    this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                this.getFlowObjectInstance(currentStateId);
            },
            reject: () => {
                this.confirmationService.close();
            }
        });
    }

    getDateString(): string {
        return new Date(this.$model().updateDate).toLocaleString();
    }

    // #region [===== private methods =====]
    // #endregion

    private pollForUpdate() {
        this.$pollingCount.set(0);
        this.interval = setInterval(() => {
            if (this.$isFinished()) {
                clearInterval(this.interval);
                this.$isPollingFinished.set(true);
                return;
            }

            if (this.MAX_RETRIES != 0 && this.$pollingCount() == this.MAX_RETRIES + 1) {
                if (this.$hasEnqueuedAtFirst() == false) {
                    this.$hasEnqueuedAtFirst.set(true);
                    this.processEnqueue();
                    this.$pollingCount.set(0);
                } else {
                    clearInterval(this.interval);
                    this.$isPollingFinished.set(true);
                    return;
                }
            }

            this.getFlowObjectInstance();
            this.$pollingCount.set(this.$pollingCount() + 1);
        }, this.RETRY_INTERVAL_SECONDS * 1000);
    }

    private async processEnqueue() {
        const response = await this.flowObjectInstanceService.enqueueTask(this.$model());

        if (!response.isSuccess) {
            this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        Utils.setSignal(this.$model, response.data);
        this._modelChangeEvent.emit(this.$model());
        this.$isPollingFinished.set(false);
        this.pollForUpdate();
    }

    private async getFlowObjectInstance(stateId?: number) {
        const response = await this.flowObjectInstanceService.getById(this.$model().id);

        if (!response.isSuccess) return;

        if (response.data.stateId == FlowObjectInstanceState.AwaitingAction) {
            clearInterval(this.interval);
            this.$isPollingFinished.set(true);
        }

        if (
            this.$model().stateId != response.data.stateId
            || (
                stateId != null
                && stateId != response.data.stateId
            )
        ) {
            Utils.setSignal(this.$model, response.data);
            this._modelChangeEvent.emit(this.$model());
        }

        this.fillMessage();
    }

    private fillMessage() {
        if (Utils.isNullOrEmpty(this.$model()?.outputData)) {
            this.$message.set('');
        } else {
            const outputData = JSON.parse(this.$model().outputData) as OutputDataTaskOutboundApi;

            if (Utils.isNotNullOrEmpty(outputData?.response?.mensagem)) {
                this.$message.set(outputData.response?.mensagem);
                this.$isMessageFromApi.set(true);
            } else if (outputData?.response?.sucesso != null && outputData?.response?.sucesso === false) {
                this.$message.set(JSON.parse(this.$model().flowObjectDefinition.configSchema).taskOutboundApi.errorMessage);
            }
        }
    }
}
